import type { Ctx } from './paint';

interface Key {
  at: number;
  color: [number, number, number];
  alpha: number;
}

/** Sky tint keyframes by minute of day, applied with `multiply`. */
const KEYS: Key[] = [
  { at: 6 * 60, color: [255, 200, 160], alpha: 0.25 },
  { at: 8 * 60, color: [255, 228, 190], alpha: 0.12 },
  { at: 11 * 60, color: [255, 255, 255], alpha: 0 },
  { at: 15 * 60, color: [255, 245, 225], alpha: 0.05 },
  { at: 17 * 60, color: [255, 190, 130], alpha: 0.22 },
  { at: 18 * 60 + 30, color: [190, 130, 170], alpha: 0.35 },
  { at: 20 * 60, color: [60, 70, 150], alpha: 0.6 },
  { at: 24 * 60, color: [35, 45, 120], alpha: 0.7 },
];

export function skyTint(minutes: number): { color: string; alpha: number; night: number } {
  let a = KEYS[0], b = KEYS[KEYS.length - 1];
  for (let i = 0; i < KEYS.length - 1; i++) {
    if (minutes >= KEYS[i].at && minutes < KEYS[i + 1].at) {
      a = KEYS[i];
      b = KEYS[i + 1];
      break;
    }
  }
  const k = b.at === a.at ? 0 : Math.max(0, Math.min(1, (minutes - a.at) / (b.at - a.at)));
  const mix = (i: number) => Math.round(a.color[i] + (b.color[i] - a.color[i]) * k);
  const alpha = a.alpha + (b.alpha - a.alpha) * k;
  const night = Math.max(0, Math.min(1, (minutes - 18 * 60) / 120));
  return { color: `rgb(${mix(0)},${mix(1)},${mix(2)})`, alpha, night };
}

import type { Light, MapDef } from '../world/mapdef';
import { T } from '../world/tiles';
import { drawGrade } from './atmosphere';

/** Window glows for a map's building facades, computed once. */
const glowCache = new Map<string, Light[]>();
function windowGlows(map: MapDef): Light[] {
  let out = glowCache.get(map.id);
  if (out) return out;
  out = [];
  for (let ty = 0; ty < map.h; ty++) {
    for (let tx = 0; tx < map.w; tx++) {
      const ch = map.rows[ty][tx];
      if ((ch === 'w' || ch === 'v') && map.rows[ty + 1]?.[tx] !== ch) out.push({ x: tx * T + 8, y: ty * T + 6, r: 26, color: '255,200,110' });
    }
  }
  glowCache.set(map.id, out);
  return out;
}

/** Weather set by the sky overlay every frame: cloud cover, wet ground, lightning. */
const weather = { gloom: 0, wet: 0, flash: 0 };
export function setWeatherLight(gloom: number, wet: number, flash: number): void {
  weather.gloom = gloom;
  weather.wet = wet;
  weather.flash = flash;
}

export interface View {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Lighting pass over the visible area: sunbeams through interior windows, sky
 * tint (multiply), lamps / lit windows / headlights (lighter), and a vignette.
 */
export function drawLighting(c: Ctx, v: View, minutes: number, map: MapDef, extra: Light[] = []): void {
  const sky = skyTint(minutes);
  const night = sky.night;

  if (map.indoor && minutes < 16 * 60 && weather.gloom < 0.9) {
    // No sunbeams on a grey day.
    const k = (minutes < 15 * 60 ? 1 : 1 - (minutes - 15 * 60) / 60) * (1 - weather.gloom);
    const slant = (minutes - 8 * 60) / 240;
    c.save();
    c.globalCompositeOperation = 'lighter';
    for (const d of map.decor) {
      if (d.kind !== 'window' || d.y > 4) continue;
      const w = d.w ?? 32;
      const g = c.createLinearGradient(d.x, d.y + 14, d.x - 20, d.y + 150);
      g.addColorStop(0, `rgba(255,236,170,${0.26 * k})`);
      g.addColorStop(1, 'rgba(255,200,110,0)');
      c.fillStyle = g;
      c.beginPath();
      c.moveTo(d.x + 3, d.y + 14);
      c.lineTo(d.x + w - 3, d.y + 14);
      c.lineTo(d.x + w - 30 - slant * 40, d.y + 150);
      c.lineTo(d.x - 30 - slant * 40, d.y + 150);
      c.closePath();
      c.fill();
    }
    c.restore();
  }

  // Indoors the lights are on, so the night tint is much softer.
  const alpha = sky.alpha * (map.indoor ? 0.45 : 1);
  if (alpha > 0.005) {
    c.save();
    c.globalCompositeOperation = 'multiply';
    c.globalAlpha = alpha;
    c.fillStyle = sky.color;
    c.fillRect(v.x, v.y, v.w, v.h);
    c.restore();
  }

  // Overcast: a cool grey dim; wet ground: a little darker and bluer (sky package).
  const day = 1 - night * 0.6;
  const dim = weather.gloom * (map.indoor ? 0.22 : 0.5) * day + weather.wet * 0.14;
  if (dim > 0.005) {
    c.save();
    c.globalCompositeOperation = 'multiply';
    c.globalAlpha = Math.min(0.6, dim);
    c.fillStyle = weather.wet > weather.gloom ? 'rgb(120,138,168)' : 'rgb(128,140,160)';
    c.fillRect(v.x, v.y, v.w, v.h);
    c.restore();
  }

  // Lamps come on early when it's dark and stormy.
  const glow = map.indoor ? Math.max(0.25, night) : Math.max(night, weather.gloom * 0.55);
  if (glow > 0) {
    c.save();
    c.globalCompositeOperation = 'lighter';
    const lights = [...map.lights, ...(map.indoor ? [] : windowGlows(map)), ...extra];
    for (const l of lights) {
      if (l.x + l.r < v.x || l.x - l.r > v.x + v.w || l.y + l.r < v.y || l.y - l.r > v.y + v.h) continue;
      const g = c.createRadialGradient(l.x, l.y, 0, l.x, l.y, l.r);
      const a = (map.indoor ? 0.16 : 0.4) * glow;
      g.addColorStop(0, `rgba(${l.color},${a})`);
      g.addColorStop(0.35, `rgba(${l.color},${a * 0.45})`);
      g.addColorStop(1, `rgba(${l.color},0)`);
      c.fillStyle = g;
      c.fillRect(l.x - l.r, l.y - l.r, l.r * 2, l.r * 2);
    }
    c.restore();
  }

  drawGrade(c, v, minutes, map.indoor, weather.gloom);

  const r = Math.max(v.w, v.h);
  const vig = c.createRadialGradient(v.x + v.w / 2, v.y + v.h / 2, r * 0.3, v.x + v.w / 2, v.y + v.h / 2, r * 0.75);
  vig.addColorStop(0, 'rgba(0,0,0,0)');
  vig.addColorStop(1, `rgba(12,6,3,${0.3 + night * 0.15 + weather.gloom * 0.08})`);
  c.fillStyle = vig;
  c.fillRect(v.x, v.y, v.w, v.h);

  // Lightning: a bluish-white flash over everything.
  if (weather.flash > 0.01) {
    c.save();
    c.globalCompositeOperation = 'lighter';
    c.fillStyle = `rgba(200,215,255,${weather.flash})`;
    c.fillRect(v.x, v.y, v.w, v.h);
    c.restore();
  }
}
